/**
 * Resolve who should appear on ring / call UI (name + avatar URL).
 * Consultant must always see the client; client must see the consultant.
 */

export function mediaUrl(img) {
  if (!img) return "";
  if (typeof img === "string") {
    const s = img.trim();
    // Guard against accidental "[object Object]"
    if (!s || s === "[object Object]") return "";
    return s;
  }
  if (typeof img === "object") {
    return (
      img.url ||
      img.secure_url ||
      img.src ||
      img.path ||
      img.location ||
      img.Location ||
      ""
    );
  }
  return "";
}

export function personName(p) {
  if (!p) return "";
  if (p.name && String(p.name).trim()) return String(p.name).trim();
  const full = `${p.firstName || p.first_name || ""} ${p.lastName || p.last_name || ""}`.trim();
  return full;
}

function asPerson(raw) {
  if (!raw || typeof raw !== "object") return null;
  const name = personName(raw);
  const image = mediaUrl(raw.image);
  const id = raw.id || raw._id || "";
  return {
    ...raw,
    id,
    name: name || raw.email || "",
    image,
    // keep raw image for callers that still expect object; also expose url string
    imageUrl: image,
  };
}

/**
 * @param {object} opts
 * @param {string} [opts.roleCode] - "consultant" | "user"
 * @param {string} [opts.myId]
 * @param {object} [opts.appointment] - appointmentData / ring payload
 * @param {object} [opts.callerData] - redux / prop alias
 * @param {object} [opts.data] - legacy JoinCallModal `data` prop
 */
export function getCallPeer({ roleCode, myId, appointment, callerData, data } = {}) {
  const appt = appointment || callerData || {};
  const nested = appt.callerData || callerData?.callerData || {};

  const clientFromDetails = appt.clientDetails
    ? {
        id: appt.clientId || appt.clientDetails._id || appt.clientDetails.id,
        firstName: appt.clientDetails.firstName,
        lastName: appt.clientDetails.lastName,
        email: appt.clientDetails.email,
        name: personName(appt.clientDetails),
        image: appt.clientDetails.image,
      }
    : null;

  const consultantFromDetails = appt.consultantDetails
    ? {
        id: appt.consultantId || appt.consultantDetails._id || appt.consultantDetails.id,
        firstName: appt.consultantDetails.firstName,
        lastName: appt.consultantDetails.lastName,
        email: appt.consultantDetails.email,
        name: personName(appt.consultantDetails),
        image: appt.consultantDetails.image,
      }
    : null;

  const client =
    asPerson(nested.user) ||
    asPerson(appt.user) ||
    asPerson(callerData?.user) ||
    asPerson(clientFromDetails) ||
    asPerson(data);

  const consultant =
    asPerson(nested.consultant) ||
    asPerson(appt.consultant) ||
    asPerson(callerData?.consultant) ||
    asPerson(consultantFromDetails);

  const uid = String(myId || "");
  const iAmConsultant =
    roleCode === "consultant" ||
    (uid &&
      (String(appt.consultantId || "") === uid ||
        String(consultant?.id || "") === uid));

  // Peer = the other party
  const peer = iAmConsultant ? client : consultant;
  const name = personName(peer) || (iAmConsultant ? "Client" : "Consultant");
  const image = mediaUrl(peer?.image) || peer?.imageUrl || "";

  return {
    iAmConsultant,
    peer: peer || null,
    client,
    consultant,
    name,
    image,
  };
}

/** Normalize ring/socket payload so callerData.user / consultant always have string name + image URL */
export function normalizeRingPayload(data = {}) {
  const nested = data.callerData || {};
  const user = asPerson(nested.user || data.user) || null;
  const consultant = asPerson(nested.consultant || data.consultant) || null;
  return {
    ...data,
    user: user || data.user,
    consultant: consultant || data.consultant,
    callerData: {
      ...nested,
      mode: nested.mode || data.mode,
      user: user
        ? {
            id: user.id,
            name: user.name,
            email: user.email,
            image: user.image || mediaUrl(user.image),
          }
        : nested.user,
      consultant: consultant
        ? {
            id: consultant.id,
            name: consultant.name,
            email: consultant.email,
            image: consultant.image || mediaUrl(consultant.image),
          }
        : nested.consultant,
    },
  };
}
